import { useCallback, useMemo } from 'react';
import { ShoppingBag, Store } from 'lucide-react';
import { Button, Divider, Dropdown, Menu, message, Tooltip } from 'antd';
import { CopyText } from 'components/LightConversation/components/CopyText';
import {
  useVoucherCardDisplay,
  VoucherCardTagInfo,
} from 'components/VoucherCard/useVoucherCardDisplay';
import { useTranslate } from '@tolgee/react';
import { cx } from '@emotion/css';
import { VoucherProductPreviewer } from 'components/VoucherCard/VoucherProductPreviewer';
import { isTiKTok } from 'lib/utils/isTikTok';
import useIM from 'hooks/business/im/useIM';
import { useIsSupportSendVoucher, useSendVoucherDisabled } from 'hooks/business/chat/useChatSide';
import { DownOutlined } from '@ant-design/icons';
import { useVoucherOperate } from 'hooks/business/useVoucherOperate';

export interface CouponItemCallbacks {
  onDelete?: (voucher: Conversation.Voucher.Vo) => void;
  onUpdate?: (voucher: Conversation.Voucher.Vo) => void;
  onEdit?: (voucher: Conversation.Voucher.Vo) => void;
  onCopyAndCreate?: (voucher: Conversation.Voucher.Vo) => void;
}

interface CouponItemProps extends CouponItemCallbacks {
  conversation: Conversation.Conversation;
  coupon: Conversation.Voucher.Vo;
}

const StatusTag = (props: {
  tagInfo: VoucherCardTagInfo;
  channel: General.PlatformUpperCaseTunnel;
  operatorNick?: string;
}) => {
  const { tagInfo, channel, operatorNick } = props;
  const { t } = useTranslate();
  const className = useMemo(() => {
    if (tagInfo.status === 'ENDED') {
      return 'text-[#515359] bg-[#f0f2f5]';
    }
    if (tagInfo.status === 'SCHEDULED') {
      return 'text-[#336fff] bg-[#d7e1fa]';
    }
    return 'text-[#00bf69] bg-[#d7f5e8]';
  }, [tagInfo.status]);

  if (channel === 'SHOPEE' && operatorNick) {
    return (
      <Tooltip title={`${t('domain.coupon.field.operator')}: ${operatorNick}`}>
        <div className={cx('text-xs rounded-sm px-1 py-[1px] ml-1 flex-shrink-0', className)}>
          {tagInfo.labelKey}
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={cx('text-xs rounded-sm px-1 py-[1px] ml-1 flex-shrink-0', className)}>
      {tagInfo.labelKey}
    </div>
  );
};

const OperationButtons = (
  props: {
    coupon: Conversation.Voucher.Vo;
    conversation: Conversation.Conversation;
    isExpired: boolean;
    className?: string;
  } & CouponItemCallbacks,
) => {
  const {
    coupon,
    conversation,
    isExpired,
    onDelete,
    onUpdate,
    onEdit,
    onCopyAndCreate,
    className,
  } = props;
  const { t } = useTranslate();
  const { channel, conversationId, controlStatus, isAffiliateSip } = conversation;
  const {
    voucherStatus,
    suspendFlag,
    currentUsage,
    usageQuantity,
    externalVoucherId,
    voucherCode: code,
  } = coupon;

  const {
    canVoid,
    canSuspendResume,
    canEdit,
    canCopy,
    voidVoucher,
    suspendResumeVoucher,
    voidLoading,
    suspendResumeLoading,
  } = useVoucherOperate(coupon, conversation);

  const supportSendVoucher = useIsSupportSendVoucher(channel);
  const { sendVoucherDisabled, sendDisabledTips } = useSendVoucherDisabled({
    channel,
    status: voucherStatus,
    suspendFlag,
    isTiktok: isTiKTok(channel),
    currentUsage,
    usageQuantity,
    controlStatus,
    isAffiliateSip,
  });
  const { sendVoucher } = useIM();

  const menuItems = useMemo(() => {
    const items: { key: string; label: string }[] = [];
    if (canVoid) {
      items.push({ key: 'void', label: t('scene.chatWorkspace.coupon.action.invalidate') });
    }
    if (canSuspendResume) {
      items.push({
        key: suspendFlag ? 'open' : 'suspend',
        label: suspendFlag
          ? t('scene.chatWorkspace.coupon.action.activate')
          : t('scene.chatWorkspace.coupon.action.suspend'),
      });
    }
    if (canEdit) {
      items.push({ key: 'edit', label: t('scene.chatWorkspace.coupon.action.edit') });
    }
    if (canCopy) {
      items.push({ key: 'copy', label: t('scene.chatWorkspace.coupon.action.duplicate') });
    }
    return items;
  }, [t, suspendFlag, canVoid, canSuspendResume, canEdit, canCopy]);

  const handleMenuClick = useCallback(
    (info: { key: string }) => {
      switch (info.key) {
        case 'void':
          voidVoucher({ onSuccess: () => onDelete?.(coupon) });
          break;
        case 'open':
        case 'suspend':
          suspendResumeVoucher({ onSuccess: (res) => onUpdate?.(res) });
          break;
        case 'edit':
          onEdit?.(coupon);
          break;
        case 'copy':
          onCopyAndCreate?.(coupon);
          break;
        default:
          break;
      }
    },
    [coupon, voidVoucher, suspendResumeVoucher, onDelete, onUpdate, onEdit, onCopyAndCreate],
  );

  const anyOperationLoading = voidLoading || suspendResumeLoading;

  if (isExpired && canCopy) {
    return (
      <Button onClick={() => handleMenuClick({ key: 'copy' })}>
        {t('scene.chatWorkspace.coupon.action.recreate')}
      </Button>
    );
  }

  if (menuItems.length === 0 && !supportSendVoucher) {
    return null;
  }

  return (
    <div className={cx('flex items-center gap-4 relative', className)}>
      {menuItems.length > 0 && (
        <Dropdown
          getPopupContainer={(triggerNode) => triggerNode.parentElement!}
          overlay={
            <Menu onClick={handleMenuClick}>
              {menuItems.map((item) => (
                <Menu.Item key={item.key} disabled={anyOperationLoading}>
                  {item.label}
                </Menu.Item>
              ))}
            </Menu>
          }
        >
          <Button disabled={anyOperationLoading}>
            {t('common.action.more')}
            <DownOutlined />
          </Button>
        </Dropdown>
      )}
      {supportSendVoucher && (
        <Tooltip title={sendDisabledTips}>
          <Button
            type="primary"
            disabled={sendVoucherDisabled}
            onClick={() => {
              sendVoucher(conversationId, {
                voucherId: externalVoucherId,
                voucherCode: code,
              }).catch(() => {
                message.error(t('scene.chatWorkspace.couponPicker.feedback.sendFailed'));
              });
            }}
          >
            {t('scene.chatWorkspace.coupon.action.send')}
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export const CouponItem = (props: CouponItemProps) => {
  const { conversation, coupon, onDelete, onUpdate, onEdit, onCopyAndCreate } = props;
  const { t } = useTranslate();
  const { storeId, channel } = conversation;
  const { voucherType, voucherName, operatorNick } = coupon;
  const {
    discount,
    tag,
    showUsageAmount,
    currentUsage,
    usageQuantity,
    showVoucherCode,
    voucherCode,
    showPerCustomerLimit,
    perCustomerLimit,
    dateText,
    showCollectStartTime,
    collectStartTimeFormatted,
    showMinBasketPrice,
    minBasketPriceText,
    showMaxPriceDetail,
    maxPriceText,
    showDisplayStartTime,
    displayStartTimeFormatted,
    showProductPreviewer,
    isExpired,
  } = useVoucherCardDisplay({
    voucher: coupon,
  });

  return (
    <div className="p-3 border border-solid border-[#F0F2F5] rounded flex items-start gap-4 group relative">
      <div className="w-[94px] h-[94px] bg-[#FFF0F0] rounded flex flex-col items-center justify-center text-[#FF4D4F] gap-2">
        {voucherType === 1 ? <Store size={28} /> : <ShoppingBag size={28} />}
        {discount ? (
          <span className="truncate max-w-[94px]" title={discount.mainText}>
            {discount.mainText}
          </span>
        ) : null}
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 inline-flex items-center min-w-0">
            <div className="inline-flex-1 text-[#232426] truncate" title={voucherName}>
              {voucherName}
            </div>
            {tag ? <StatusTag tagInfo={tag} channel={channel} operatorNick={operatorNick} /> : null}
          </div>
          {showProductPreviewer && (
            <VoucherProductPreviewer storeId={storeId} voucher={coupon} zIndex={1051}>
              <Button size="small">{t('domain.coupon.field.eligibleProducts')}</Button>
            </VoucherProductPreviewer>
          )}
        </div>
        <div className="flex flex-col text-xs text-[#84868C] gap-1 mt-1">
          {showUsageAmount || showVoucherCode ? (
            <div className="flex items-center">
              {showUsageAmount && (
                <span>
                  {currentUsage}/{usageQuantity}
                </span>
              )}
              {showVoucherCode && (
                <>
                  <Divider type="vertical" className="mx-1" />
                  <CopyText showIcon text={voucherCode} />
                </>
              )}
            </div>
          ) : null}
          {showPerCustomerLimit && (
            <div>
              {t('domain.coupon.field.usageLimitPerUser')}: {perCustomerLimit}
            </div>
          )}
          {showCollectStartTime && (
            <div>
              {t('domain.coupon.field.claimPeriod')}: {collectStartTimeFormatted}
            </div>
          )}
          {showMinBasketPrice && (
            <div>
              {t('domain.coupon.field.minSpend')}: {minBasketPriceText}
            </div>
          )}
          {showMaxPriceDetail && (
            <div>
              {t('domain.coupon.field.maxDiscount')}: {maxPriceText}
            </div>
          )}
          {showDisplayStartTime && (
            <div>
              {t('domain.coupon.field.enablePreview')}: {displayStartTimeFormatted}
            </div>
          )}
          <div>{dateText}</div>
        </div>
      </div>
      <div
        className={cx(
          'flex flex-col gap-2 absolute right-3 top-1/2 -translate-y-1/2 z-10',
          showProductPreviewer ? 'items-end' : 'items-center justify-center',
        )}
      >
        <OperationButtons
          className="invisible group-hover:visible"
          coupon={coupon}
          conversation={conversation}
          onDelete={onDelete}
          onUpdate={onUpdate}
          onEdit={onEdit}
          onCopyAndCreate={onCopyAndCreate}
          isExpired={isExpired}
        />
      </div>
    </div>
  );
};
